import { Edge, Node } from "@xyflow/react";

import { Variable } from "../InputVariableConfigurator/types";
import { RawBlockType } from "../PowerBlock/Blocks";

import { OperatorType } from "./WorkflowCanvas/Canvas/ContextMenu/Conditions/constants";

// Use RawBlockType directly for workflow node types
export { RawBlockType as BlockType };

export enum WorkflowNodeStatus {
  QUEUED = "QUEUED",
  RUNNING = "RUNNING",
  COMPLETED = "SUCCESS",
  FAILED = "FAILED",
  SKIPPED = "SKIPPED",
}

interface WorkflowProviderApiKeyPrompt {
  provider: string;
  retryMode: "run" | "play_from_here";
}

export enum EdgeStatusType {
  PASSED = "PASSED",
  FAILED = "FAILED",
}

interface EdgeStatus {
  id: number;
  edge_id: string;
  workflow_version_execution_id: number;
  status: EdgeStatusType;
  error_message: string | null;
}

interface WorkflowNodeConfiguration {
  function_name?: string;
  value?: string;
  first_source?: string;
  second_source?: string;
  sources?: string[];
  source?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  template?: any;
  loop_type?: "prompt" | "workflow";
  position?: { x: number; y: number };
  prompt_template_variable_mappings?: Record<string, string>;
}
interface StaticConfig {
  static_value: string;
}

interface ReferenceConfig {
  source: {
    name: string;
    json_path?: string | null;
  };
}

type ValueConfig = StaticConfig | ReferenceConfig;

interface EdgeCondition {
  id: string;
  position: number;
  left_config: ValueConfig;
  operator: OperatorType;
  right_config: ValueConfig;
}

interface WorkflowEdge {
  id: string;
  conditionals: EdgeCondition[];
  workflow_version_id: number;
  source_node_name: string;
  target_node_name: string;
  is_and: boolean;
  is_loading?: boolean; // FE only prop
  status?: EdgeStatusType; // FE only prop
  saved?: boolean; // FE only prop
}

interface WorkflowNode {
  id: string;
  name: string;
  node_type: RawBlockType;
  configuration: WorkflowNodeConfiguration;
  dependencies: string[];
  status?: WorkflowNodeStatus;
  workflow_version_id?: number;
  is_output_node: boolean;
  saved?: boolean;
  has_default_name?: boolean;
}

type InputVariables = Record<string, string>;

interface WorkflowVersion {
  id: number;
  workflow_id: number;
  number: number;
  created_at: string;
  commit_message: string;
  user_id: number;
  required_input_variables?: InputVariables;
  user_email: string;
  labels: WorkflowLabel[];
  archived: boolean;
}

export interface WorkflowLabel {
  id: number;
  created_at: string;
  name: string;
  workflow_version_id: number;
  workflow_id: number;
  is_protected: boolean;
  approver_role_id: number | null;
}

interface WorkflowVersionExecution {
  id: string;
  workflow_version_id: number;
  input_variables: object;
  created_at: string;
  trace_id?: string;
  metadata?: object;
  has_error?: boolean;
  error_summary?: string | null;
  error_at?: string | null;
}

interface WorkflowNodeOutput {
  id: string;
  workflow_node_id: string;
  workflow_version_execution_id: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any;
  display_value: string;
  created_at: string;
  status: WorkflowNodeStatus;
}

interface WorkflowInfo {
  id: number;
  name?: string;
  workspace_id: number;
  tags?: string[];
}

interface Workflow extends WorkflowInfo {
  workflow_id: number | null;
  commit_message: string;
  nodes: WorkflowNode[];
  input_variables: Variable[];
  created_at?: string;
  version_id?: number | null;
  folder_id: number | null;
}

interface WorkflowVersionDetailResponse {
  edges?: WorkflowEdge[];
  required_input_variables?: InputVariables;
  message?: string;
  success: boolean;
}

interface WorkflowNodesResponse {
  workflow_nodes?: WorkflowNode[];
  message?: string;
  success: boolean;
}

interface WorkflowVersionResponse {
  workflow_versions?: WorkflowVersion[];
  message?: string;
  success: boolean;
}

type WorkflowNodesRequest = [key: string, workflow_version_id: number | string, userToken: string];

type WorkflowVersionRequest = [key: string, workflow_id: string, userToken: string];

interface WorkflowNodeOutputPayload {
  created_at: string | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  display_value: any | null;
  error_message: string | null;
  id: number;
  is_draft: boolean;
  status: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any | null;
  workflow_node_id: number;
  workflow_node_name: string;
}

type SetNodes = React.Dispatch<React.SetStateAction<Node[]>>;
type SetEdges = React.Dispatch<React.SetStateAction<Edge[]>>;

interface ExecutionStatus {
  display_value: DisplayValue;
  error_message: string | null;
  id: string;
  is_draft: boolean;
  status: WorkflowNodeStatus;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any | null;
  workflow_node_id: string;
  workflow_node_name: string;
  workflow_version_execution_id: number;
}

type DisplayValue =
  | {
      type: "string";
      value: string;
    }
  | {
      type: "number";
      value: number;
    }
  | {
      type: "boolean";
      value: boolean;
    }
  | {
      type: "object";
      value: object;
    }
  | {
      type: "unknown";
      value: unknown;
    }
  | {
      type: "output_media";
      value: { type?: string; url?: string; media_type?: string };
    };

interface NodeOutput {
  date: number;
  display_value: DisplayValue;
  request_id?: number;
  trace_id?: string;
}

interface ObservableWorkflowNode extends WorkflowNode {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  errors?: any[];
  outputs?: NodeOutput[];
  providerApiKeyPrompt?: WorkflowProviderApiKeyPrompt | null;
  triggered?: boolean;
}

interface WorkflowSchema {
  workflow_id: number | null;
  workspace_id?: number;
  name: string;
  commit_message: string;
  folder_id?: number | null;
  nodes: WorkflowNode[];
  edges: {
    source_node_name: string | null;
    target_node_name: string | null;
    is_and: boolean;
    conditionals: {
      position: number;
      operator: EdgeCondition["operator"];
      left_config: {
        type: "source" | "static_value";
      } & ValueConfig;
      right_config: {
        type: "source" | "static_value";
      } & ValueConfig;
    }[];
  }[];
  required_input_variables: Record<string, string | object>;
  is_draft: boolean;
}

export type {
  WorkflowSchema,
  DisplayValue,
  EdgeStatus,
  ExecutionStatus,
  InputVariables,
  NodeOutput,
  ObservableWorkflowNode,
  SetEdges,
  SetNodes,
  Workflow,
  WorkflowInfo,
  WorkflowNode,
  WorkflowNodeOutput,
  WorkflowNodeOutputPayload,
  WorkflowProviderApiKeyPrompt,
  WorkflowVersionDetailResponse,
  WorkflowNodesRequest,
  WorkflowNodesResponse,
  WorkflowVersion,
  WorkflowVersionExecution,
  WorkflowVersionRequest,
  WorkflowVersionResponse,
  WorkflowEdge,
  EdgeCondition,
  ValueConfig,
  ReferenceConfig,
};

export { OperatorType };
